/**
 * useSidecarMeta — fetch the `.meta.json` sidecar for a GCS audio artifact.
 *
 * Best-practice pattern for progressive enhancement:
 * - New artifacts: sidecar is co-produced by the worker (zero latency).
 * - Old artifacts: sidecar is backfilled by a one-time migration script.
 * - Missing sidecar: returns null, frontend gracefully degrades (no crash, no blocking).
 *
 * The sidecar lives at `{gsUri}.meta.json` and is fetched through the existing
 * `/bff/gcs/json` proxy endpoint.
 */

import { useQuery } from '@tanstack/react-query';
import { bff } from '@/lib/http/bff';

/** Single beat / downbeat event published by analysis workers. */
export interface BeatEvent {
  /** Timestamp in seconds from track start. */
  t: number;
  /** Event kind. Workers may emit additional types in the future. */
  type: 'beat' | 'downbeat' | string;
}

/** Beat-grid summary written by the BeatNet handler (`media.beat_tracking`). */
export interface BeatGridSummary {
  total_beats?: number;
  total_downbeats?: number;
  duration_sec?: number;
  /** GCS URI of the human-readable timestamps txt artifact (optional). */
  artifact_gs?: string;
  method?: string;
  model_number?: number;
  inference_mode?: string;
  computed_at?: number;
}

/**
 * Forward-compatible analysis slot. Each lens is single-responsibility and
 * additive — readers should treat unknown keys as opaque.
 *
 * Mirrors worker_core/sidecar_meta.py contract.
 */
export interface SidecarAnalysis {
  bpm?: { value?: number; confidence?: number; method?: string };
  key?: { tonic?: string; mode?: 'major' | 'minor' | string; confidence?: number };
  beat_grid?: BeatGridSummary;
  /** Inline beat/downbeat events. Compact representation for ≤ ~15-min tracks. */
  beats?: BeatEvent[];
  loudness?: { lufs_integrated?: number; true_peak_db?: number };
  stems?: Record<string, string>;
  [key: string]: unknown;
}

/** Schema mirrors `sidecar_meta.py SIDECAR_SCHEMA_VERSION` (currently 2). */
export interface SidecarMeta {
  v: number;
  duration_ms?: number;
  sample_rate_hz?: number;
  channels?: number;
  codec?: string;
  container?: string;
  bitrate_kbps?: number;
  title?: string;
  artist?: string;
  album?: string;
  source_url?: string;
  /** Present only for stem sidecars */
  stem?: string;
  /** Present only for stem sidecars */
  source_gcs_uri?: string;
  /** Present only for backfilled entries */
  backfilled?: boolean;
  created_at?: number;
  updated_at?: number;
  /** Forward-compatible on-demand analysis lenses (sidecar v2+). */
  analysis?: SidecarAnalysis;
  [key: string]: unknown;
}

/** Optional overrides for polling behaviour — used when analysis is in-flight. */
export interface UseSidecarMetaOptions {
  /**
   * Refetch interval in ms. Pass `false` (default) to disable polling.
   * Set to e.g. `5_000` while a worker is writing to the sidecar so the UI
   * picks up new analysis data within seconds.
   */
  refetchInterval?: number | false;
  /**
   * Override the stale threshold. Defaults to 5 min (sidecar is immutable in
   * steady state). Set to `0` together with `refetchInterval` to force
   * TanStack Query to honour the refetch across shared observers.
   */
  staleTime?: number;
}

/**
 * Fetch the sidecar metadata for a `gs://` audio URI.
 *
 * @param gsUri - The GCS URI of the audio file (e.g. `gs://bucket/path/song.opus`).
 *   The sidecar is expected at `gs://bucket/path/song.opus.meta.json`.
 * @param options - Optional polling / caching overrides for in-flight analysis.
 * @returns `{ data, isLoading, error }` — `data` is `SidecarMeta | null`.
 *   Returns `null` if the sidecar doesn't exist (404) — this is expected for
 *   old artifacts that haven't been backfilled yet.
 */
export function useSidecarMeta(
  gsUri: string | undefined | null,
  options?: UseSidecarMetaOptions,
) {
  const sidecarUri = gsUri ? `${gsUri}.meta.json` : '';

  return useQuery<SidecarMeta | null>({
    queryKey: ['buckets', 'sidecarMeta', sidecarUri] as const,
    queryFn: async () => {
      if (!sidecarUri) return null;
      try {
        const res = await bff.get<{ ok: boolean; artifact?: SidecarMeta; error?: string }>(
          '/gcs/json',
          { gsUri: sidecarUri },
        );
        if (res?.ok && res.artifact && typeof res.artifact === 'object') {
          return res.artifact as SidecarMeta;
        }
        return null;
      } catch (e: any) {
        // 404 is expected — the sidecar simply doesn't exist (old artifact, not backfilled).
        // Don't throw, just return null so the UI gracefully degrades.
        const status = e?.status ?? e?.statusCode ?? e?.response?.status;
        if (status === 404) return null;
        // Other errors (network, 5xx) — also return null to avoid blocking the audio player.
        // The audio plays fine without metadata.
        return null;
      }
    },
    enabled: Boolean(sidecarUri),
    // When an observer passes staleTime:0, TanStack Query's min-staleTime rule
    // makes all observers treat data as immediately stale → refetch is triggered.
    staleTime: options?.staleTime ?? 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: false,
    refetchInterval: options?.refetchInterval ?? false,
  });
}
